import { useMemo, useState } from "react";
import { Link, useLoaderData, useSubmit } from "react-router-dom";

import Footer from "src/components/common/frame/footer";
import FileIcon from "src/components/common/fileIcons";
import { showSize } from "src/components/common/functions";
import { useAuthStore, User } from "src/components/main/authStore";

import { NewspaperLoaderData, NewspaperItem, NEWSPAPER_ROOT_ID } from "./actions_loaders";

export default function NewspaperList() {
    const { currentFolderId, currentNewspaperId, items, parents } = useLoaderData() as NewspaperLoaderData;
    const user = useAuthStore(state => state.user) as User;
    const submit = useSubmit();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [filterWords, setFilterWords] = useState("");
    const canEdit = user.newspaper === "edit";
    const filteredItems = useMemo(
        () => items.filter(item => JSON.stringify(item).toLowerCase().includes(filterWords.toLowerCase())),
        [items, filterWords]
    );
    const currentItems = filteredItems.slice((currentPage - 1) * Number(itemsPerPage), currentPage * Number(itemsPerPage));

    const mutate = (kind: "createFolder" | "rename" | "delete" | "setCurrent", values: Record<string, string>) => {
        const formData = new FormData();
        formData.append("rootFolderId", NEWSPAPER_ROOT_ID);
        Object.entries(values).forEach(([key, value]) => formData.append(key, value));
        submit(formData, {
            method: "post",
            action: `_action/${kind}`,
        });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-center flex-wrap gap-3">
                <div className = "fw-bold text-success">
                    {parents.map((parent, index) => (
                        <span key = {parent.id}>
                            {index ? <span className = "mx-2">&rarr;</span> : null}
                            {parent.id === currentFolderId ? (
                                <span>{parent.name}</span>
                            ) : (
                                <Link to = {parent.id === NEWSPAPER_ROOT_ID ? "/Newspaper" : `/Newspaper/${parent.id}`}>{parent.name}</Link>
                            )}
                        </span>
                    ))}
                </div>
                <label htmlFor = "newspaper-search" className = "mb-0">Search</label>
                <input
                    id = "newspaper-search"
                    className = "form-control inline w-auto"
                    value = {filterWords}
                    onChange = {e => {
                        setCurrentPage(1);
                        setFilterWords(e.currentTarget.value);
                    }}
                />
                {canEdit ? (
                    <>
                        <Link className = "btn btn-outline-primary" to = {`Add?folderId=${currentFolderId}`}>Add Newspaper</Link>
                        <button
                            type = "button"
                            className = "btn btn-outline-primary"
                            onClick = {() => {
                                let folderName = window.prompt("Folder Name:");
                                if (folderName === null) return;
                                while (folderName === "") {
                                    window.alert("Folder Name can not be empty.");
                                    folderName = window.prompt("Folder Name:");
                                    if (folderName === null) return;
                                }
                                mutate("createFolder", { parentFolderId: currentFolderId, folderName });
                            }}
                        >
                            Add Folder
                        </button>
                    </>
                ) : null}
            </nav>
            {!filteredItems.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Size</th>
                                <th />
                                {canEdit ? <th /> : null}
                                {canEdit ? <th /> : null}
                                {canEdit ? <th /> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(item => (
                                <tr key = {item.id}>
                                    <td>
                                        {item.type === "folder" ? (
                                            <Link to = {`/Newspaper/${item.id}`} className = "fw-bold text-primary">
                                                <FileIcon fileType = "" isFolder = {true} className = "me-2" />
                                                {item.name}
                                            </Link>
                                        ) : (
                                            <span>
                                                <FileIcon fileType = "html" isFolder = {false} className = "me-2" />
                                                {item.name}
                                                {currentNewspaperId === item.id ? <span className = "ms-2 badge text-bg-primary">pop</span> : null}
                                            </span>
                                        )}
                                    </td>
                                    <td>{item.type === "folder" ? "Folder" : "Newspaper"}</td>
                                    <td>{item.type === "folder" ? "" : showSize(item.size)}</td>
                                    <td>{renderViewButton(item, currentFolderId, canEdit)}</td>
                                    {canEdit ? (
                                        <td>
                                            {item.type === "folder" ? (
                                                <button
                                                    type = "button"
                                                    className = "btn btn-outline-secondary"
                                                    onClick = {() => {
                                                        let fileName = window.prompt("Name:", item.name);
                                                        if (fileName === null) return;
                                                        while (fileName === "") {
                                                            window.alert("The name cannot be empty.");
                                                            fileName = window.prompt("Name:", item.name);
                                                            if (fileName === null) return;
                                                        }
                                                        mutate("rename", { fileId: item.id, fileName });
                                                    }}
                                                >
                                                    Rename
                                                </button>
                                            ) : null}
                                        </td>
                                    ) : null}
                                    {canEdit ? (
                                        <td>
                                            <button
                                                type = "button"
                                                className = "btn btn-danger"
                                                onClick = {() => {
                                                    if (!window.confirm("Are you sure to delete this document?")) return;
                                                    mutate("delete", { fileId: item.id });
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    ) : null}
                                    {canEdit ? (
                                        <td>
                                            {item.type !== "folder" ? (
                                                <button
                                                    type = "button"
                                                    className = {`btn btn-${currentNewspaperId !== item.id ? "success" : "danger"}`}
                                                    onClick = {() => {
                                                        if (!window.confirm("Are you sure to change the current newspaper?")) return;
                                                        mutate("setCurrent", { fileId: currentNewspaperId !== item.id ? item.id : "" });
                                                    }}
                                                >
                                                    {currentNewspaperId !== item.id ? "Set Pop" : "Cancel Pop"}
                                                </button>
                                            ) : null}
                                        </td>
                                    ) : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {filteredItems.length ? (
                <Footer
                    length = {filteredItems.length}
                    currentPage = {currentPage}
                    setCurrentPage = {setCurrentPage}
                    itemsPerPage = {itemsPerPage}
                    setItemsPerPage = {setItemsPerPage}
                />
            ) : null}
        </div>
    );
}

function renderViewButton(item: NewspaperItem, currentFolderId: string, canEdit: boolean) {
    if (item.type === "folder") return null;
    const mode = canEdit ? "Edit" : "View";
    return <Link className = "btn btn-outline-primary" to = {`/Newspaper/Article/${item.id}/${mode}?folderId=${currentFolderId}`}>{mode}</Link>;
}
